/**
 * ContextBanner — "Watching this screen" live indicator.
 * Shows what the agent "sees" based on current route context.
 */
import { useLocation } from "react-router-dom";
import { AgentOrb } from "./AgentOrb";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getPageContext } from "./pageContextConfig";
import { useTeeAgentStore } from "@/zustand/useTeeAgentStore";

interface ContextBannerProps {
  className?: string;
}

export const ContextBanner = ({ className }: ContextBannerProps) => {
  const location = useLocation();
  const { agentState, screenContext } = useTeeAgentStore();
  const pageContext = getPageContext(location.pathname);

  // Build context chips from screen context data or fallback to page name
  const chips: string[] = [];
  if (screenContext?.chips) {
    chips.push(...screenContext.chips);
  } else {
    chips.push(pageContext.pageName);
  }

  const summary = screenContext?.summary || `Viewing ${pageContext.pageName}`;

  return (
    <div
      className={cn(
        "px-4 py-3 border-b flex items-start gap-3 border-gray6 bg-gradient-to-b from-gray7/50 to-white",
        className
      )}
    >
      <AgentOrb state={agentState} size={22} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 text-gray3">
          <span>Watching this screen</span>
          <span className="w-1 h-1 rounded-full bg-gray5" />
          <span className="text-green2">{pageContext.pageName}</span>
        </div>
        <div className="text-[12px] mt-1 text-gray2">{summary}</div>
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {chips.map((c) => (
              <Badge
                key={c}
                variant="secondary"
                className="text-[10px] px-1.5 py-0.5 font-semibold bg-gray7 text-gray3"
              >
                {c}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
